import { useCallback, useEffect, useState } from 'react';
import { CSSProperties } from 'styled-components';

import { getDynamicFloors } from '@src/api/room';
import socket from '@src/lib/socket';

const colorSchemaStyle: any = {
  default: {
    background: 'green', // green
    color: 'black',
  },
  bookedNotOccupied: {
    background: 'green', // green
    border: '2px solid red',
    color: 'black',
  },
  bookedOccupied: {
    background: 'red', // red
    border: '2px solid red',
    color: 'black',
  },
  notBookedOccupied: {
    background: 'red', // red
    border: '2px solid blue',
    color: 'black',
  },
};

const FloorPage = () => {
  const [floors, setFloors] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<number | null>(null);
  const BASE_WIDTH = 100;

  const [zoom, setZoom] = useState(1);

  const zoomIn = () => setZoom(z => Math.min(z + 0.2, 3));
  const zoomOut = () => setZoom(z => Math.max(z - 0.2, 0.6));

  const fetchFloors = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      const data = await getDynamicFloors();

      const filterData: Record<string, number[]> = {};

      data?.forEach((item: any) => {
        const loc = item?.location?.name;

        if (!loc) return;

        if (!filterData[loc]) filterData[loc] = [];

        if (!filterData[loc].includes(item.floorNumber)) {
          filterData[loc].push(item.floorNumber);
        }
      });
      const firstLoc = Object.keys(filterData)[0];
      setSelectedLocation(firstLoc);
      setSelectedFloor(filterData[firstLoc][0]);
      setFilterOptions(filterData);
      setFloors(data);
    } catch (err) {
      console.error('Error fetching floors', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  const filteredFloors = floors.filter(f => {
    const locMatch = selectedLocation ? f.location?.name === selectedLocation : true;
    const floorMatch = selectedFloor ? f.floorNumber === selectedFloor : true;
    return locMatch && floorMatch;
  });

  useEffect(() => {
    fetchFloors();

    socket.on('welcome', (data: any) => console.log('Server says:', data));
    socket.on('ha-event', () => fetchFloors(false));

    return () => {
      socket.off('welcome');
      socket.off('ha-event');
    };
  }, [fetchFloors]);

  const zoomBtnStyle: CSSProperties = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    background: '#333',
    color: '#fff',
    boxShadow: `
    0 0 6px rgba(255,255,255,0.6),
    0 0 12px rgba(255,255,255,0.5),
    0 0 20px rgba(255,255,255,0.4)
  `,
    transition: 'all 0.25s ease',
  };

  const chipStyle: CSSProperties = {
    background: '#68c3e1',
    padding: '6px 8px',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#fff',
    fontWeight: 600,
    textAlign: 'center',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const swatchStyle = (background: string): CSSProperties => ({
    width: '16px',
    height: '16px',
    borderRadius: '3px',
    background,
    border: '1px solid rgba(0,0,0,0.25)',
    flexShrink: 0,
  });

  const legendRowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#333',
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        maxHeight: '100%',
      }}
    >
      <div style={{ gridColumn: 'span 2', padding: '10px', borderRight: '1px solid #acabb1' }}>
        {/* LOCATION */}
        Location
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '10px',
            marginTop: '5px',
            marginBottom: '10px',
          }}
        >
          {Object.keys(filterOptions).map((locName, index) => (
            <div
              style={{
                ...chipStyle,
                background: selectedLocation === locName ? '#68c3e1' : '#406f81',
              }}
              onClick={() => {
                setSelectedLocation(locName);
                setSelectedFloor(null);
              }}
              key={index}
            >
              {locName}
            </div>
          ))}
        </div>
        Floors
        <div
          style={{
            display: 'grid',
            marginTop: '5px',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '10px',
          }}
        >
          {(selectedLocation ? filterOptions[selectedLocation] : []).map((fno, i) => (
            <div
              style={{
                ...chipStyle,
                background: selectedFloor == fno ? '#68c3e1' : '#406f81',
              }}
              onClick={() => setSelectedFloor(fno)}
              key={i}
            >
              Floor {fno}
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT SIDE (FLOOR IMAGE) */}
      {/* RIGHT SIDE */}
      <div
        style={{
          gridColumn: '3 / -1',
          height: '90vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* SCROLL CONTAINER */}

        {!loading ? (
          <>
            <div
              style={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
              }}
            >
              {/* IMAGE + GRID WRAPPER */}
              <div
                style={{
                  position: 'relative',
                  width: `${BASE_WIDTH * zoom}%`, // 🔥 zoom works here
                  margin: zoom === 1 ? '0 auto' : '0', // 🔑 KEY FIX
                }}
              >
                {/* IMAGE */}
                <img
                  src={filteredFloors[0]?.floorImage?.url}
                  alt='Floor Map'
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />

                {/* GRID OVERLAY */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(100, 1fr)',
                    gridTemplateRows: 'repeat(100, 1fr)',
                    pointerEvents: 'none',
                  }}
                >
                  {filteredFloors[0]?.roomConfigs?.map((roomConfig: any, index: number) => (
                    <div
                      key={`${index}-${roomConfig?.roomNumber}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '900',
                        fontSize: '14px',
                        ...colorSchemaStyle[roomConfig?.roomState],
                        ...roomConfig?.roomFloorConfig,
                      }}
                    >
                      Room {roomConfig?.roomNumber}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ZOOM CONTROLS */}
            <div
              style={{
                position: 'absolute',
                top: 20,
                right: 30,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                zIndex: 10,
              }}
            >
              <button onClick={zoomIn} style={zoomBtnStyle}>
                +
              </button>
              <button onClick={zoomOut} style={zoomBtnStyle}>
                −
              </button>
            </div>

            {/* LEGEND */}
            <div
              style={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                background: 'rgba(255,255,255,0.92)',
                padding: '10px 14px',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                zIndex: 10,
                boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '12px', color: '#333' }}>Legend</div>
              <div style={legendRowStyle}>
                <span style={swatchStyle('red')} /> Occupied
              </div>
              <div style={legendRowStyle}>
                <span style={swatchStyle('green')} /> Un-occupied
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default FloorPage;
